const MASK_KEY = 'msr_key_mask';
const MASKED_KEY_PREFIX = 'msr:';

/**
 * Convert Uint8Array to base64 string.
 */
function bytesToBase64(bytes: Uint8Array): string {
  return btoa(String.fromCharCode(...bytes));
}

/**
 * Convert base64 string to Uint8Array.
 */
function base64ToBytes(base64: string): Uint8Array {
  const binary = atob(base64);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i++) {
    bytes[i] = binary.charCodeAt(i);
  }
  return bytes;
}

/**
 * XOR two byte arrays of equal length.
 */
function xorBytes(a: Uint8Array, b: Uint8Array): Uint8Array {
  if (a.length !== b.length) {
    throw new Error('XOR operands must have equal length');
  }
  const result = new Uint8Array(a.length);
  for (let i = 0; i < a.length; i++) {
    result[i] = a[i] ^ b[i];
  }
  return result;
}

/**
 * Persist encryption key for session using split-key pattern.
 * Splits key into two parts using XOR:
 * - Mask stored in sessionStorage (survives refresh, tab-scoped)
 * - Masked key stored in window.name (survives refresh, not persisted to disk)
 *
 * Neither part alone reveals the key. Both are needed to reconstruct.
 */
export function persistKeyForSession(key: Uint8Array): void {
  // Generate random mask
  const mask = crypto.getRandomValues(new Uint8Array(key.length));

  // XOR key with mask to create masked key
  const maskedKey = xorBytes(key, mask);

  // Store mask in sessionStorage
  sessionStorage.setItem(MASK_KEY, bytesToBase64(mask));

  // Store masked key in window.name with prefix
  window.name = MASKED_KEY_PREFIX + bytesToBase64(maskedKey);
}

/**
 * Restore encryption key from session storage.
 * Returns null if key is not available (new session or cleared).
 */
export function restoreKeyFromSession(): Uint8Array | null {
  const maskB64 = sessionStorage.getItem(MASK_KEY);
  const windowName = window.name;

  // Check if both parts exist
  if (!maskB64 || !windowName.startsWith(MASKED_KEY_PREFIX)) {
    return null;
  }

  try {
    const mask = base64ToBytes(maskB64);
    const maskedKey = base64ToBytes(windowName.slice(MASKED_KEY_PREFIX.length));

    // XOR to recover original key
    return xorBytes(maskedKey, mask);
  } catch {
    // If parsing fails, clear and return null
    clearSessionKey();
    return null;
  }
}

/**
 * Clear session key (on logout or tab close).
 */
export function clearSessionKey(): void {
  sessionStorage.removeItem(MASK_KEY);
  window.name = '';
}

/**
 * Check if a session key is persisted.
 */
export function hasPersistedKey(): boolean {
  return (
    sessionStorage.getItem(MASK_KEY) !== null &&
    window.name.startsWith(MASKED_KEY_PREFIX)
  );
}
